import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { BRAND_COLORS } from "../lib/bookingStatus";
import { deDateShort } from "../lib/dateFormat";

export default function TeamAssignments() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [properties, setProperties] = useState([]);
  const [partners, setPartners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ partner_email: "", property_id: "", title: "", description: "", kind: "task", scheduled_date: "" });
  const [assigning, setAssigning] = useState(null);

  const load = async () => {
    const [a, p, pt] = await Promise.all([api.get("/assignments"), api.get("/properties"), api.get("/partners")]);
    setRows(a.data); setProperties(p.data); setPartners(pt.data);
  };
  useEffect(() => { load(); }, []);

  const assignTo = async (id, email) => {
    if (!email) return;
    setAssigning(id);
    try {
      await api.patch(`/assignments/${id}/assign`, { partner_email: email });
      toast.success("Partner zugewiesen.");
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
    finally { setAssigning(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/assignments", form);
      toast.success("Auftrag angelegt.");
      setForm({ partner_email: "", property_id: "", title: "", description: "", kind: "task", scheduled_date: "" });
      setShowForm(false);
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-assignments">
      <div className="flex items-start justify-between gap-6 mb-8 flex-wrap">
        <div>
          <div className="label-caps mb-3">Weissblom Team</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">Partner-Aufträge</h1>
          <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">Aufträge an externe Partner vergeben und deren Status verfolgen.</p>
        </div>
        <button data-testid="new-assignment-btn" onClick={() => setShowForm((v) => !v)}
          className="text-ivory px-8 py-3 text-[12px] tracking-[0.15em] uppercase hover:opacity-90"
          style={{ background: BRAND_COLORS.navy }}>{showForm ? "Schließen" : "Neuer Auftrag"}</button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="assignment-form"
          className="bg-white border border-navy/10 p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <input required data-testid="partner-email-input" type="email" placeholder="Partner E-Mail" value={form.partner_email}
            onChange={(e) => setForm({ ...form, partner_email: e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />
          <select required data-testid="property-select" value={form.property_id}
            onChange={(e) => setForm({ ...form, property_id: e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3">
            <option value="">Objekt wählen</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input required data-testid="assignment-title-input" placeholder="Titel"
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />
          <select data-testid="kind-select" value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3">
            <option value="task">Aufgabe</option>
            <option value="cleaning">Reinigung</option>
            <option value="garden">Garten</option>
            <option value="maintenance">Wartung</option>
          </select>
          <input required data-testid="scheduled-date-input" type="date" value={form.scheduled_date}
            onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
            className="bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />
          <textarea rows={2} placeholder="Beschreibung"
            value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="md:col-span-2 bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3" />
          <button data-testid="assignment-submit-btn" type="submit"
            className="md:col-span-2 text-ivory py-3 text-[12px] tracking-[0.15em] uppercase hover:opacity-90"
            style={{ background: BRAND_COLORS.navy }}>Auftrag senden</button>
        </form>
      )}

      <div className="bg-white border border-navy/10">
        <div className="grid grid-cols-7 gap-4 p-4 border-b border-navy/10 label-caps text-[11px]">
          <div>Datum</div>
          <div>Objekt</div>
          <div className="col-span-2">Titel</div>
          <div>Partner</div>
          <div>Status</div>
          <div>Bewertung</div>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-[13px] text-navy-dark/50 text-center">Noch keine Aufträge vergeben.</div>
        ) : rows.map((r) => (
          <div key={r.id} data-testid={`team-assignment-row-${r.id}`}
            className="grid grid-cols-7 gap-4 p-4 border-b border-navy/5 last:border-b-0 text-[13px] items-center">
            <div className="text-navy-dark">{deDateShort(r.scheduled_date)}</div>
            <div className="text-navy-dark truncate">{r.property_name}</div>
            <div className="col-span-2 text-navy font-medium truncate">{r.title}</div>
            <div className="text-navy-dark/70 truncate">
              {r.partner_email ? (
                <span data-testid={`asg-partner-${r.id}`}>{r.partner_email}</span>
              ) : (
                <select
                  data-testid={`asg-assign-select-${r.id}`}
                  disabled={assigning === r.id}
                  defaultValue=""
                  onChange={(e) => assignTo(r.id, e.target.value)}
                  className="w-full bg-white border border-gold text-navy px-2 py-1 text-[12px]"
                >
                  <option value="">Partner wählen…</option>
                  {partners.map((p) => (
                    <option key={p.id} value={p.email}>{p.name || p.email}</option>
                  ))}
                </select>
              )}
            </div>
            <div className="label-caps text-[10.5px]">{t.requests.status[r.status] || r.status}</div>
            <div className="text-navy" data-testid={`asg-rating-${r.id}`}>
              {r.rating
                ? <span title={r.rating_comment || ""}>{"★".repeat(r.rating)}<span className="text-navy-dark/20">{"★".repeat(5 - r.rating)}</span></span>
                : <span className="text-navy-dark/30 text-[11px]">—</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
